import React, { useEffect, useRef, useState } from 'react';

interface IPickerNavigation {
    /** Сколько пунктов в списке. */
    count: number;
    /** Смена значения (новый поиск, другая вкладка) — подсветка с первой строки. */
    resetKey: unknown;
    /** Строки ещё не под текущий поиск: Enter по ним не выбирает. */
    pending: boolean;
    /** Enter по подсвеченному пункту. */
    onChoose: (index: number) => void;
    /** Ctrl+Enter — полный список или окно выбора. */
    onOpenMore?: () => void;
    onClose: () => void;
}

/**
 * Подсветка и клавиатура выпадающего списка с поиском: стрелки, Home/End (в строке поиска — курсор), Enter
 * (Ctrl+Enter — выход в окно), Escape и Tab закрывают. Подсвеченный пункт прокручивается в видимую область списка
 * (`data-index`). Refs строки поиска и списка — отсюда.
 */
export function usePickerNavigation({ count, resetKey, pending, onChoose, onOpenMore, onClose }: IPickerNavigation) {
    const [active, setActive] = useState(0);
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // новый поиск — подсветка с первой строки; свежий ответ на тот же поиск её не сбрасывает
    useEffect(() => setActive(0), [resetKey]);
    useEffect(() => setActive((i) => Math.min(i, Math.max(0, count - 1))), [count]);

    useEffect(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
    }, [active]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        // кнопки подвала («Готово», «Полный список») нажимаются сами, а не выбирают подсвеченную строку
        if ((e.key === 'Enter' || e.key === ' ') && e.target instanceof HTMLButtonElement) return;
        const last = Math.max(0, count - 1);
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActive((i) => Math.min(last, i + 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActive((i) => Math.max(0, i - 1));
                break;
            case 'Home':
            case 'End':
                if (e.target === searchRef.current) return;
                e.preventDefault();
                setActive(e.key === 'Home' ? 0 : last);
                break;
            case 'Enter':
                e.preventDefault();
                if ((e.ctrlKey || e.metaKey) && onOpenMore) {
                    onOpenMore();
                } else if (active < count && !pending) {
                    onChoose(active);
                }
                break;
            case 'Escape':
            case 'Tab':
                e.preventDefault();
                e.stopPropagation();
                onClose();
                break;
        }
    };

    return { active, setActive, searchRef, listRef, onKeyDown };
}
